import React, { useMemo } from 'react';

const MomentumTracker = ({ allPlays, awayAbbr, homeAbbr }) => {
  const segments = useMemo(() => {
    if (!allPlays?.length) return [];
    const result = [];
    let awayScore = 0;
    let homeScore = 0;
    let lastInning = 0;

    allPlays.forEach((play) => {
      const inning = play.about?.inning || 0;
      const rbi = play.result?.rbi || 0;
      const event = play.result?.event || '';
      const isTop = play.about?.isTopInning;

      if (rbi > 0 || event === 'Home Run') {
        if (inning !== lastInning) lastInning = inning;
        if (isTop) awayScore += rbi || 1;
        else homeScore += rbi || 1;

        result.push({
          inning,
          isTop,
          event,
          rbi,
          awayScore,
          homeScore,
          lead: homeScore - awayScore,
          desc: play.result?.description || '',
        });
      }
    });

    return result;
  }, [allPlays]);

  if (!segments.length) return (
    <div className="text-[#658d71] text-xs text-center py-6 uppercase tracking-widest">
      No scoring data available
    </div>
  );

  const maxScore = Math.max(...segments.map(s => Math.max(s.awayScore, s.homeScore)), 1);

  return (
    <div className="flex flex-col gap-3">
      {/* Lead change timeline */}
      <div className="flex flex-col gap-1">
        {segments.map((seg, i) => {
          const isLeadChange = i > 0 && Math.sign(seg.lead) !== Math.sign(segments[i - 1].lead);
          const isTied = seg.lead === 0;
          return (
            <div key={i} className={`flex items-start gap-2 py-1.5 px-2 border-l-2 ${
              isLeadChange ? 'border-[#d8ceb3] bg-[rgba(216,206,179,0.06)]' :
              isTied ? 'border-[#658d71] bg-[rgba(101,141,113,0.06)]' :
              seg.isTop ? 'border-[rgba(169,201,177,0.4)]' : 'border-[rgba(196,51,62,0.4)]'
            }`}>
              <div className="shrink-0 w-10 text-center">
                <span className="text-[9px] font-bold text-[#a9c9b1] uppercase">
                  {seg.isTop ? '▲' : '▼'}{seg.inning}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-[10px] font-black uppercase tracking-wider ${
                    seg.event === 'Home Run' ? 'text-[#c4333e]' : 'text-[#d8ceb3]'
                  }`}>{seg.event}</span>
                  {seg.rbi > 0 && (
                    <span className="text-[9px] bg-[#c4333e] text-white px-1 font-bold">{seg.rbi} RBI</span>
                  )}
                  {isLeadChange && (
                    <span className="text-[9px] bg-[#d8ceb3] text-[#1e3a29] px-1 font-black uppercase tracking-widest">
                      Lead Change
                    </span>
                  )}
                  {isTied && (
                    <span className="text-[9px] bg-[#658d71] text-white px-1 font-black uppercase tracking-widest">
                      Tied
                    </span>
                  )}
                  <span className="ml-auto text-[10px] font-black text-[#a9c9b1] tabular-nums">
                    {awayAbbr} {seg.awayScore} – {seg.homeScore} {homeAbbr}
                  </span>
                </div>
                <p className="text-[9px] text-[#658d71] leading-tight mt-0.5 line-clamp-1">{seg.desc}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Score progression bars */}
      <div className="border border-[#658d71] p-2 bg-[rgba(0,0,0,0.1)]">
        <p className="text-[9px] uppercase tracking-widest text-[#658d71] font-bold mb-2">Score Progression</p>
        <div className="flex flex-col gap-1.5">
          {[
            { label: awayAbbr, color: '#a9c9b1', score: segments[segments.length - 1]?.awayScore || 0 },
            { label: homeAbbr, color: '#c4333e', score: segments[segments.length - 1]?.homeScore || 0 },
          ].map(({ label, color, score }) => (
            <div key={label} className="flex items-center gap-2">
              <span className="text-[9px] font-black uppercase w-8 shrink-0" style={{ color }}>{label}</span>
              <div className="flex-1 h-4 bg-[rgba(0,0,0,0.3)] rounded-sm overflow-hidden">
                <div
                  className="h-full flex items-center justify-end pr-1 transition-all duration-700"
                  style={{ width: `${(score / maxScore) * 100}%`, backgroundColor: color, opacity: 0.8 }}
                >
                  <span className="text-[9px] font-black text-white tabular-nums">{score}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default MomentumTracker;